import React, { useState } from 'react';
import { Mail, Send, CalendarCheck, CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { SEO } from '../components/SEO';
// Replace "your-form-id" with the form ID from your Formspree dashboard
// (Formspree -> your form -> the endpoint looks like https://formspree.io/f/abcdwxyz).
const FORMSPREE_FORM_ID = 'your-form-id';
const FORMSPREE_ENDPOINT = `https://formspree.io/f/${FORMSPREE_FORM_ID}`;
const COUNTRIES = ['United States', 'Canada', 'United Kingdom', 'Australia', 'India', 'Germany', 'France', 'Singapore', 'United Arab Emirates', 'Other'];
export function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [intent, setIntent] = useState<'quote' | 'consultation'>('quote');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'error'>('idle');
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>, type: 'quote' | 'consultation') => {
    e.preventDefault();
    setIntent(type);
    setStatus('submitting');
    const form = e.currentTarget;
    const formData = new FormData(form);
    formData.set('intent', type);
    formData.set('_subject', type === 'quote' ? 'New quote request from hireliocareerservices.com' : 'New consultation request from hireliocareerservices.com');
    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json'
        }
      });
      if (!response.ok) throw new Error('Submission failed');
      form.reset();
      setSubmitted(true);
      setStatus('idle');
      setTimeout(() => setSubmitted(false), 6000);
    } catch (err) {
      setStatus('error');
    }
  };
  return <div className="w-full pt-20 pb-24 bg-slate-50 dark:bg-navy-900/50 min-h-screen" data-id="element-231">
      <SEO title="Contact Hirelio | Request a Resume Writing Quote" description="Get in touch with Hirelio Career Services. Request a personalized quote or schedule a free consultation with a professional resume writer." data-id="element-232" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20" data-id="element-233">
        <div className="grid lg:grid-cols-2 gap-16" data-id="element-234">
          {/* Contact Info */}
          <div data-id="element-235">
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-navy-900 dark:text-white mb-6" data-id="element-236">
              Get in Touch
            </h1>
            <p className="text-lg text-slate-600 dark:text-slate-400 mb-12 leading-relaxed" data-id="element-237">
              Have questions about our services or need help choosing the right
              package? Request a personalized quote or schedule a free
              consultation with a career expert.
            </p>

            <div className="space-y-8" data-id="element-238">
              <div className="flex items-start gap-4" data-id="element-239">
                <div className="w-12 h-12 bg-white dark:bg-navy-800 rounded-xl flex items-center justify-center text-brand shrink-0 shadow-sm border border-slate-100 dark:border-navy-700" data-id="element-240">
                  <Mail size={24} data-id="element-241" />
                </div>
                <div data-id="element-242">
                  <h3 className="font-heading font-bold text-navy-900 dark:text-white text-lg mb-1" data-id="element-243">
                    Email Us
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 mb-1" data-id="element-244">
                    We aim to respond within 24 hours.
                  </p>
                  <a href="mailto:Hello@hireliocareerservices.com" className="text-brand font-medium hover:underline" data-id="element-245">
                    Hello@hireliocareerservices.com
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-12 flex items-center gap-3 text-sm text-slate-500 dark:text-slate-400" data-id="element-246">
              <CheckCircle2 size={18} className="text-emerald-500 shrink-0" data-id="element-247" />
              Your information is encrypted and never shared with third parties.
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white dark:bg-navy-800 rounded-3xl p-8 md:p-10 shadow-xl border border-slate-100 dark:border-navy-700" data-id="element-248">
            {submitted ? <div className="h-full flex flex-col items-center justify-center text-center py-12 animate-in fade-in zoom-in duration-300" data-id="element-249">
                <div className="w-20 h-20 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-500 rounded-full flex items-center justify-center mb-6" data-id="element-250">
                  <CheckCircle2 size={40} data-id="element-251" />
                </div>
                <h3 className="text-2xl font-heading font-bold text-navy-900 dark:text-white mb-2" data-id="element-252">
                  {intent === 'quote' ? 'Quote Request Received!' : 'Consultation Requested!'}
                </h3>
                <p className="text-slate-600 dark:text-slate-400 max-w-sm" data-id="element-253">
                  {intent === 'quote' ? 'Thanks for reaching out. A career specialist will email your personalized quote within 24 hours.' : 'Thanks! We will reach out shortly to confirm a time for your free consultation.'}
                </p>
              </div> : <form className="space-y-5" onSubmit={e => handleSubmit(e, intent)} data-id="element-254">
                <div data-id="element-255">
                  <label htmlFor="contact-name" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2" data-id="element-256">
                    Name
                  </label>
                  <input id="contact-name" name="name" required type="text" autoComplete="name" className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-navy-600 bg-slate-50 dark:bg-navy-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-brand/50 focus:border-brand outline-none transition-all" data-id="element-257" />
                </div>

                <div className="grid md:grid-cols-2 gap-5" data-id="element-258">
                  <div data-id="element-259">
                    <label htmlFor="contact-email" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2" data-id="element-260">
                      Email
                    </label>
                    <input id="contact-email" name="email" required type="email" autoComplete="email" className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-navy-600 bg-slate-50 dark:bg-navy-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-brand/50 focus:border-brand outline-none transition-all" data-id="element-261" />
                  </div>
                  <div data-id="element-262">
                    <label htmlFor="contact-phone" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2" data-id="element-263">
                      Phone
                    </label>
                    <input id="contact-phone" name="phone" type="tel" autoComplete="tel" className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-navy-600 bg-slate-50 dark:bg-navy-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-brand/50 focus:border-brand outline-none transition-all" data-id="element-264" />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5" data-id="element-265">
                  <div data-id="element-266">
                    <label htmlFor="contact-country" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2" data-id="element-267">
                      Country
                    </label>
                    <select id="contact-country" name="country" className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-navy-600 bg-slate-50 dark:bg-navy-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-brand/50 focus:border-brand outline-none transition-all" data-id="element-268">
                      {COUNTRIES.map(c => <option key={c} data-id="element-269">{c}</option>)}
                    </select>
                  </div>
                  <div data-id="element-270">
                    <label htmlFor="contact-title" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2" data-id="element-271">
                      Current Job Title
                    </label>
                    <input id="contact-title" name="jobTitle" type="text" autoComplete="organization-title" className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-navy-600 bg-slate-50 dark:bg-navy-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-brand/50 focus:border-brand outline-none transition-all" data-id="element-272" />
                  </div>
                </div>

                <div data-id="element-273">
                  <label htmlFor="contact-message" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2" data-id="element-274">
                    Message
                  </label>
                  <textarea id="contact-message" name="message" required rows={4} placeholder="Tell us about your career goals and which services you're interested in..." className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-navy-600 bg-slate-50 dark:bg-navy-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-brand/50 focus:border-brand outline-none transition-all resize-none placeholder:text-slate-400" data-id="element-275"></textarea>
                </div>

                {status === 'error' && <div className="flex items-start gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-xl px-4 py-3" data-id="element-276">
                    <AlertCircle size={18} className="shrink-0 mt-0.5" data-id="element-277" />
                    Something went wrong sending your message. Please try again
                    or email us directly at Hello@hireliocareerservices.com.
                  </div>}

                <div className="flex flex-col sm:flex-row gap-3 pt-1" data-id="element-278">
                  <button type="submit" disabled={status === 'submitting'} onClick={() => setIntent('quote')} className="flex-1 bg-brand hover:bg-brand-600 text-white py-4 rounded-xl font-medium transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed" data-id="element-279">
                    {status === 'submitting' && intent === 'quote' ? <>
                        Sending <Loader2 size={18} className="animate-spin" data-id="element-280" />
                      </> : <>
                        Request Quote <Send size={18} data-id="element-281" />
                      </>}
                  </button>
                  <button type="submit" disabled={status === 'submitting'} onClick={() => setIntent('consultation')} className="flex-1 bg-slate-100 dark:bg-navy-700 hover:bg-slate-200 dark:hover:bg-navy-600 text-navy-900 dark:text-white py-4 rounded-xl font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed" data-id="element-282">
                    {status === 'submitting' && intent === 'consultation' ? <>
                        Sending <Loader2 size={18} className="animate-spin" data-id="element-283" />
                      </> : <>
                        Schedule Consultation <CalendarCheck size={18} data-id="element-284" />
                      </>}
                  </button>
                </div>
              </form>}
          </div>
        </div>
      </div>
    </div>;
}